import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2, ArrowRight, Headset, Users, Mail, PhoneOutgoing, Bell, AlertTriangle, MessageSquare, ThumbsUp, ShoppingCart } from 'lucide-react';

const services = [
  {
    id: 'call-handling',
    icon: Headset,
    name: 'Call Handling',
    tagline: 'Professional answering service 24/7/365',
    description: 'Our core service ensures that every call your business receives is answered promptly and professionally. We act as your switchboard, taking messages, answering basic FAQs, and escalating urgent issues according to your custom protocols.',
    who: 'For businesses that receive high call volumes and cannot afford to let clients hit voicemail.',
    benefits: [
      'Eliminate voicemail drop-offs entirely',
      'Every caller greeted with your bespoke script',
      'Instant message delivery via email or SMS',
      'Seamless overflow during your peak hours',
    ],
    useCase: 'A growing IT support company receives surges of calls during outages. Instead of clients hearing an engaged tone, Cygnet operators answer, log the ticket details, reassure the client, and push the priority ticket to the IT team\'s dashboard.',
    image: 'https://images.pexels.com/photos/8937607/pexels-photo-8937607.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1',
  },
  {
    id: 'virtual-receptionist',
    icon: Users,
    name: 'Virtual Receptionist',
    tagline: 'Dedicated front-of-house representation',
    description: 'More than just message taking. Our virtual receptionists get to know your business intimately. They can manage your diary, screen nuisance calls, process simple payments, and act entirely as an in-house team member would.',
    who: 'For service businesses, solo practitioners, and clinics needing a personal touch.',
    benefits: [
      'Direct calendar integration for appointment booking',
      'Intelligent caller screening and VIP routing',
      'Handling of complex, multi-step customer inquiries',
      'Provides the appearance of a large, established office',
    ],
    useCase: 'A private dental clinic\'s in-house receptionist is occupied with a patient. A new patient calls to book an urgent assessment. The Cygnet virtual receptionist answers as the clinic, accesses their booking system, books the slot, and takes the deposit.',
    image: 'https://images.pexels.com/photos/7709235/pexels-photo-7709235.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1',
  },
  {
    id: 'email-monitoring',
    icon: Mail,
    name: 'Email Monitoring',
    tagline: 'Rapid response inbox management',
    description: 'Customer service isn\'t just about phone calls. We monitor your generic inboxes (info@, support@) ensuring strict SLAs are met. We categorize emails, draft responses from templates, and flag urgent issues to your on-call staff.',
    who: 'For businesses that struggle to maintain response times, especially out-of-hours.',
    benefits: [
      'Strict adherence to your response time SLAs',
      'Triaging and categorization of incoming requests',
      'Drafting responses for standard inquiries',
      'Emergency escalation pathways for critical emails',
    ],
    useCase: 'An e-commerce brand receives hundreds of "where is my order" emails over the weekend. Instead of a massive backlog on Monday morning, Cygnet operators monitor the inbox, check tracking numbers, and reply to customers in real-time.',
    image: 'https://images.pexels.com/photos/4021808/pexels-photo-4021808.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1',
  },
  {
    id: 'duty-of-care',
    icon: PhoneOutgoing,
    name: 'Duty of Care Calls',
    tagline: 'Proactive customer relationship management',
    description: 'Our Duty of Care outbound call service provides proactive welfare check-ins, appointment reminders, and follow-up calls for your existing customers. This is not a sales service — it is a care service.',
    who: 'For businesses looking to improve retention, reduce no-shows, or nurture relationships.',
    benefits: [
      'Dramatically reduce costly appointment no-shows',
      'Gather valuable post-service customer feedback',
      'Maintain relationships with dormant clients',
      'Check-in on vulnerable customers',
    ],
    useCase: 'A home care provider wants to ensure their clients are safe and happy. Cygnet operators make polite, non-intrusive welfare check-in calls to ensure a carer arrived on time and the client is doing well.',
    image: 'https://images.pexels.com/photos/7564287/pexels-photo-7564287.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1',
  },
  {
    id: 'appointment-reminders',
    icon: Bell,
    name: 'Appointment Reminder Calls',
    tagline: 'Reduce no-shows and protect your revenue',
    description: 'No-shows are one of the most costly problems for appointment-based businesses. Our outbound reminder calls give clients advance notice, confirm attendance, and allow reschedules in time for you to refill the slot — all handled by our team so yours doesn\'t have to.',
    who: 'For clinics, trades, consultants, salons, and any business where a missed appointment means lost revenue.',
    benefits: [
      'Significant reduction in costly no-show rates',
      'Automated scheduling of reminder calls to your timetable',
      'Allows clients to reschedule without tying up your staff',
      'Frees your team to focus on delivering the service',
    ],
    useCase: 'A physiotherapy clinic with 40 appointments per week was losing 6–8 to no-shows each month. After introducing Cygnet reminder calls 48 hours before each appointment, no-show rates dropped by over 70% within the first month.',
    image: 'https://images.pexels.com/photos/4386466/pexels-photo-4386466.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1',
  },
  {
    id: 'emergency-line',
    icon: AlertTriangle,
    name: 'After-Hours Emergency Line',
    tagline: 'Urgent-only coverage when your office is closed',
    description: 'Not every out-of-hours call is an emergency — but the ones that are need to reach someone immediately. Our after-hours emergency line filters genuine urgent calls from routine ones, escalating only what truly cannot wait until morning.',
    who: 'For businesses in trades, facilities, healthcare, IT, or any sector where emergencies don\'t keep office hours.',
    benefits: [
      'Genuine emergencies reach your on-call team instantly',
      'Routine calls logged and delivered the next morning',
      'Strict escalation protocols defined by you',
      'Your team only disturbed when it truly matters',
    ],
    useCase: 'An IT managed service provider gives their clients a single after-hours number. Cygnet triage the calls — a server being slow is logged for morning, but a complete system outage is escalated to the on-call engineer within minutes.',
    image: 'https://images.pexels.com/photos/3183197/pexels-photo-3183197.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1',
  },
  {
    id: 'complaint-handling',
    icon: MessageSquare,
    name: 'Complaint First Response',
    tagline: 'Defuse issues before they escalate',
    description: 'How a complaint is first received often determines whether it becomes a resolution or a review. Our operators are trained to handle dissatisfied customers with empathy and professionalism — acknowledging the issue, logging every detail, and setting clear expectations for follow-up.',
    who: 'For businesses in retail, utilities, logistics, and any customer-facing sector where complaint management matters.',
    benefits: [
      'Every complaint acknowledged promptly and professionally',
      'Full complaint details logged and sent to your team',
      'Angry callers de-escalated before reaching senior staff',
      'Consistent, compliant complaint intake process',
    ],
    useCase: 'A logistics company receiving delivery complaints uses Cygnet as the first point of contact. Operators acknowledge the issue, apologise on behalf of the company, take full details, and raise an internal ticket — meaning the client feels heard before a manager even gets involved.',
    image: 'https://images.pexels.com/photos/7709082/pexels-photo-7709082.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1',
  },
  {
    id: 'satisfaction-calls',
    icon: ThumbsUp,
    name: 'Outbound Satisfaction Calls',
    tagline: 'Turn completed jobs into loyal clients',
    description: 'Following up after a service is one of the most effective ways to build loyalty, gather feedback, and generate referrals — yet most businesses never do it. Cygnet makes outbound post-service calls on your behalf, checking satisfaction and strengthening the relationship.',
    who: 'For trades, healthcare, professional services, and any business where repeat custom and referrals drive growth.',
    benefits: [
      'Genuine feedback gathered while the experience is fresh',
      'Identifies dissatisfied clients before they leave a bad review',
      'Strengthens client relationships with a personal touch',
      'Referral opportunities naturally surfaced in conversation',
    ],
    useCase: 'A roofing company asks Cygnet to call every completed job customer three days after the work is done. The calls take two minutes, generate a steady stream of five-star reviews, and have resulted in several direct referrals — at no extra cost to the business.',
    image: 'https://images.pexels.com/photos/3760093/pexels-photo-3760093.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1',
  },
  {
    id: 'order-taking',
    icon: ShoppingCart,
    name: 'Order Taking',
    tagline: 'Never miss a sale due to an unanswered phone',
    description: 'For retail, food, and wholesale businesses, every missed call during trading hours is a missed sale. Our order-taking service ensures every inbound order call is answered, processed accurately, and confirmed back to the customer — integrated with your existing systems.',
    who: 'For food businesses, wholesalers, retailers, and any business that takes orders by phone.',
    benefits: [
      'Every order captured accurately, even during peak periods',
      'Orders logged directly into your system or sent by email',
      'Upsell prompts included in your custom call script',
      'Covers lunch rushes, evenings, and overflow periods',
    ],
    useCase: 'A catering supplies wholesaler receives most of their orders by phone between 8 AM and 10 AM. During that peak window their lines were frequently busy. Cygnet handles the overflow, ensuring no order is lost and every customer is served promptly.',
    image: 'https://images.pexels.com/photos/4393668/pexels-photo-4393668.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1',
  },
];

const Services = () => {
  const { hash } = useLocation();

  useEffect(() => {
    if (!hash) return;
    const el = document.getElementById(hash.replace('#', ''));
    if (el) setTimeout(() => {
      const top = el.getBoundingClientRect().top + window.scrollY - 90;
      window.scrollTo({ top, behavior: 'smooth' });
    }, 100);
  }, [hash]);

  return (
  <div className="pb-12">
    {/* Header */}
    <section className="bg-slate-900 text-white py-16 md:py-20 relative overflow-hidden">
      <div className="mx-auto max-w-3xl px-6 relative z-10 text-center">
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6"
        >
          Services built around your needs.
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="text-xl text-white/70 mb-8"
        >
          From simple overflow message taking to comprehensive virtual front-of-house, we scale to match your operational requirements.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="flex flex-col sm:flex-row gap-4 justify-center"
        >
          <Link to="/contact" className="inline-flex items-center justify-center rounded-md bg-white text-cygnet-dark px-6 py-3 text-base font-semibold hover:bg-gray-100 transition-colors">
            Get in Touch
          </Link>
          <a href="tel:+442085385400" className="inline-flex items-center justify-center rounded-md border border-white/30 text-white px-6 py-3 text-base font-medium hover:bg-white/10 transition-colors">
            Call 0208 538 5400
          </a>
        </motion.div>
      </div>
    </section>

    {/* Service details */}
    {services.map((service, index) => (
      <section
        key={service.id}
        id={service.id}
        className={`py-16 md:py-24 scroll-mt-20 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
      >
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-100px' }}
              className={`flex flex-col lg:flex-row gap-8 md:gap-12 items-start ${index % 2 !== 0 ? 'lg:flex-row-reverse' : ''}`}
            >
              {/* Text */}
              <div className="lg:w-1/2 space-y-6 order-1 lg:order-none">
                <div className="inline-flex items-center gap-3 px-4 py-2 rounded-full bg-gray-100 text-gray-700 font-medium text-sm border border-gray-200">
                  <service.icon className="w-4 h-4 text-cygnet-dark" />
                  {service.tagline}
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900">{service.name}</h2>
                <p className="text-lg text-gray-500 leading-relaxed">{service.description}</p>
                <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                  <h4 className="font-semibold text-gray-900 mb-4">Core Benefits:</h4>
                  <ul className="space-y-3">
                    {service.benefits.map((benefit, i) => (
                      <motion.li key={i} whileHover={{ x: 4 }} transition={{ duration: 0.2 }} className="flex items-start gap-3 text-gray-500">
                        <CheckCircle2 className="w-5 h-5 text-cygnet-dark shrink-0 mt-0.5" />
                        <span>{benefit}</span>
                      </motion.li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Image panel */}
              <div className="lg:w-1/2 w-full order-2 lg:order-none">
                <div className="relative rounded-2xl overflow-hidden shadow-lg min-h-[400px]">
                  <img src={service.image} alt={service.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-slate-900/80" />
                  <div className="relative z-10 p-8 h-full flex flex-col justify-center text-white space-y-8">
                    <div>
                      <div className="text-sm uppercase tracking-wider text-white/50 font-semibold mb-2">Ideal For</div>
                      <p className="text-lg font-medium">{service.who}</p>
                    </div>
                    <div className="bg-white/10 backdrop-blur-md border border-white/10 rounded-xl p-6">
                      <div className="text-sm uppercase tracking-wider text-white/50 font-semibold mb-3">Example Scenario</div>
                      <p className="italic text-white/80 leading-relaxed">"{service.useCase}"</p>
                    </div>
                    <div>
                      <Link to="/contact" className="inline-flex items-center gap-2 justify-center w-full rounded-md bg-white text-cygnet-dark px-5 py-3 text-sm font-bold hover:bg-gray-100 transition-colors">
                        Get in Touch <ArrowRight className="w-4 h-4" />
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
        </div>
      </section>
    ))}

    {/* Footer CTA */}
    <section className="py-16 md:py-20 bg-slate-900 text-white">
      <div className="mx-auto max-w-3xl px-6 text-center">
        <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Not sure which mix is right for you?</h2>
        <p className="text-lg text-white/70 mb-8">
          Every business is unique. Contact our team for a no-obligation consultation where we'll design a custom communication strategy based on your volume and needs. Call{' '}
          <a href="tel:+442085385400" className="text-white underline hover:text-white/80">0208 538 5400</a>{' '}or enquire online.
        </p>
        <Link to="/contact" className="inline-flex items-center justify-center rounded-md bg-white text-cygnet-dark px-8 py-4 text-base font-semibold hover:bg-gray-100 transition-colors">
          Get in Touch <ArrowRight className="ml-2 w-5 h-5" />
        </Link>
      </div>
    </section>
  </div>
  );
};

export default Services;
